import { AuthState, IDToken } from "@okta/okta-auth-js";
import { getEnvVariables } from "lib/environment";
import crypto from "crypto-js";
import { v4 as uuidv4 } from "uuid";

const APP_SESSION_LENGTH = 20;
const LOGIN_SESSION_KEY = "jw-gis-login";
const REAUTH_STATE_VALUE = "forceReauth";

const getAppSessionToken = (): string => {
  return localStorage.getItem(LOGIN_SESSION_KEY) || "";
};

const deleteAppSessionToken = () => {
  localStorage.removeItem(LOGIN_SESSION_KEY);
};

const setAppSessionToken = (sessionToken: string) => {
  localStorage.setItem(LOGIN_SESSION_KEY, sessionToken);
};

const isValidSessionToken = (
  currentDateStr: string,
  clientId: string,
  sessionToken: string,
) => {
  const targetSessionTokenIdentifier = createSessionTokenId(
    currentDateStr,
    clientId,
  );
  return targetSessionTokenIdentifier === sessionToken;
};

const hasValidAppSession = (
  idToken: IDToken | undefined,
  currentAppSessionUser: string,
  now: Date,
) => {
  if (!idToken) {
    return false;
  }

  // The user found in the session token does not match the user that is currently logged in
  if (idToken.claims.sub !== currentAppSessionUser) {
    return false;
  }

  const auth_time = idToken.claims.auth_time;
  if (!auth_time) {
    // This should never be the case; id token should always have an auth_time claim
    return false;
  }

  const session_expiration = new Date(
    (auth_time + APP_SESSION_LENGTH * 60) * 1000,
  );
  if (session_expiration < now) {
    // The application session has expired
    return false;
  }

  return true;
};

const createSessionTokenId = (
  currentDateStr: string,
  clientId: string,
): string => {
  const sessionTokenIdentifier = crypto
    .SHA256(clientId + ":" + currentDateStr)
    .toString(crypto.enc.Hex);
  return sessionTokenIdentifier;
};

const getAppSession = (authState: AuthState | null): string => {
  const sessionToken = getAppSessionToken();
  if (!sessionToken) {
    return "";
  }

  const [sessionTokenIdentifier, oktaUid] = sessionToken.split(":");
  const clientId = getEnvVariables().oktaAppClientId;
  const currentDateStr = new Date().toDateString();

  if (!isValidSessionToken(currentDateStr, clientId, sessionTokenIdentifier)) {
    deleteAppSessionToken();
  } else if (
    authState &&
    authState.isAuthenticated &&
    !hasValidAppSession(authState.idToken, oktaUid, new Date())
  ) {
    deleteAppSessionToken();
  }
  return getAppSessionToken();
};

const setAppSession = (authState: AuthState) => {
  const idToken = authState.idToken;
  const oktaUid = idToken?.claims.sub;
  const clientId = idToken?.clientId || "";
  const currentDateStr = new Date().toDateString();
  const sessionTokenIdentifier = createSessionTokenId(currentDateStr, clientId);
  const sessionToken = sessionTokenIdentifier + ":" + oktaUid;

  setAppSessionToken(sessionToken);
};

// When max_age=0 is used, the state parameter is used to store when the reauth was initiated
const createReauthState = (now: number): string => {
  const reauthTime = now / 1000;
  return REAUTH_STATE_VALUE + ":" + reauthTime + ":" + uuidv4();
};

// This ensures that the user did not tamper with the max_age parameter
const hasValidReauthTime = (
  searchParams: URLSearchParams,
  authState: AuthState,
): boolean => {
  const reauthTime = searchParams.get("state")?.split(":")[1];
  const auth_time = authState.idToken?.claims.auth_time;

  if (!reauthTime || !auth_time) {
    return false;
  }

  return auth_time > parseInt(reauthTime);
};

// The state parameter can be inspected to determine if max_age=0 was used when redirecting to Okta
const hasReauthState = (searchParams: URLSearchParams): boolean => {
  return (
    searchParams.get("state")?.split(":").includes(REAUTH_STATE_VALUE) || false
  );
};

export {
  createSessionTokenId,
  isValidSessionToken,
  hasValidAppSession,
  setAppSession,
  getAppSession,
  createReauthState,
  hasValidReauthTime,
  hasReauthState,
  REAUTH_STATE_VALUE,
};
